import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import {
  ContentCopy as TemplateIcon,
  Speed as SpeedIcon,
  Tune as SettingsIcon,
  Payments as PayoutIcon,
} from '@mui/icons-material';
import { EducationModal, type EducationStep } from '../shared/EducationModal';

// Visual: Template concept
const TemplateConceptVisual: React.FC = () => (
  <Box sx={{ textAlign: 'center' }}>
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
      <Paper elevation={2} sx={{ p: 1.5, bgcolor: 'primary.50', border: 2, borderColor: 'primary.main', borderStyle: 'dashed' }}>
        <Typography variant="caption" fontWeight="bold" color="primary.main">Template</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2px', mt: 0.5 }}>
          {[...Array(9)].map((_, i) => (
            <Box key={i} sx={{ width: 12, height: 12, bgcolor: 'primary.200', borderRadius: '2px' }} />
          ))}
        </Box>
      </Paper>
      <Typography variant="h5" color="text.secondary">→</Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[1, 2, 3].map((n) => (
          <Paper key={n} elevation={1} sx={{ p: 1, minWidth: 40 }}>
            <Typography variant="caption" display="block" textAlign="center">New</Typography>
            <Typography variant="caption" display="block" textAlign="center" fontWeight="bold">#{n}</Typography>
          </Paper>
        ))}
      </Box>
    </Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
      One template, many boards
    </Typography>
  </Box>
);

// Visual: Time savings
const TimeSavingsVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" display="block">Without Template</Typography>
      <Typography variant="h5" color="error.main">10+ min</Typography>
      <Typography variant="caption" color="text.secondary">Setup time</Typography>
    </Box>
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" display="block">With Template</Typography>
      <Typography variant="h5" color="success.main">30 sec</Typography>
      <Typography variant="caption" color="text.secondary">Setup time</Typography>
    </Box>
  </Box>
);

// Visual: What's saved
const WhatsSavedVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
    {[
      { label: 'Payouts', icon: '💰' },
      { label: 'Settings', icon: '⚙️' },
      { label: 'Players', icon: '👥' },
      { label: 'Theme', icon: '🎨' },
    ].map((item) => (
      <Paper key={item.label} elevation={0} sx={{ px: 1.5, py: 1, bgcolor: 'grey.50', border: 1, borderColor: 'grey.200', textAlign: 'center' }}>
        <Typography variant="body1">{item.icon}</Typography>
        <Typography variant="caption" display="block">{item.label}</Typography>
      </Paper>
    ))}
  </Box>
);

// Visual: Create methods
const CreateMethodsVisual: React.FC = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
    <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200', textAlign: 'center', minWidth: 100 }}>
      <Typography variant="caption" fontWeight="bold" color="primary.main" display="block">From Scratch</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>Build new template</Typography>
    </Paper>
    <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'success.50', border: 1, borderColor: 'success.200', textAlign: 'center', minWidth: 100 }}>
      <Typography variant="caption" fontWeight="bold" color="success.main" display="block">From Board</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>Save existing setup</Typography>
    </Paper>
  </Box>
);

const steps: EducationStep[] = [
  {
    title: 'Board Templates',
    description: 'Save your favorite board configurations as reusable templates. Create new boards in seconds instead of setting everything up from scratch.',
    icon: <TemplateIcon sx={{ fontSize: 40 }} />,
    visual: <TemplateConceptVisual />,
  },
  {
    title: 'Save Time',
    description: 'Stop repeating the same setup every week. Templates remember your payout structure, settings, and even pre-assigned players.',
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    visual: <TimeSavingsVisual />,
  },
  {
    title: 'Everything Included',
    description: 'Templates save your complete board configuration: payout percentages, profit settings, theme selection, and player assignments.',
    icon: <SettingsIcon sx={{ fontSize: 40 }} />,
    visual: <WhatsSavedVisual />,
  },
  {
    title: 'Two Ways to Create',
    description: 'Build a new template from scratch, or save an existing board as a template to reuse its exact configuration later.',
    icon: <PayoutIcon sx={{ fontSize: 40 }} />,
    visual: <CreateMethodsVisual />,
  },
];

interface BoardTemplatesEducationModalProps {
  open: boolean;
  onClose: () => void;
}

export const BoardTemplatesEducationModal: React.FC<BoardTemplatesEducationModalProps> = ({ open, onClose }) => (
  <EducationModal
    open={open}
    onClose={onClose}
    modalId="board_templates"
    steps={steps}
    headerColor="warning.main"
    finishButtonText="View Templates"
    buttonColor="warning"
  />
);

export default BoardTemplatesEducationModal;
